import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  getPlanningDuplicateOptions,
  PlanningDuplicateCascadeOptions,
  PlanningDuplicateEntityType,
} from '../../types/PlanningDuplicateScope';
import { ModalPortalShell } from '../ui/ModalPortalShell';

type PlanningDuplicateModalProps = {
  open: boolean;
  entityType: PlanningDuplicateEntityType;
  itemLabel: string;
  counts?: {
    objectives?: number;
    metas?: number;
    marcos?: number;
    tasks?: number;
  };
  onClose: () => void;
  onConfirm: (options: PlanningDuplicateCascadeOptions) => Promise<void>;
};

const EMPTY_OPTIONS: PlanningDuplicateCascadeOptions = {
  includeObjectives: false,
  includeMetas: false,
  includeMarcos: false,
  includeTasks: false,
};

export function PlanningDuplicateModal({
  open,
  entityType,
  itemLabel,
  counts,
  onClose,
  onConfirm,
}: PlanningDuplicateModalProps) {
  const [options, setOptions] = useState<PlanningDuplicateCascadeOptions>(EMPTY_OPTIONS);
  const [saving, setSaving] = useState(false);

  const cascadeOptions = useMemo(
    () => getPlanningDuplicateOptions(entityType, counts),
    [entityType, counts],
  );

  useEffect(() => {
    if (!open) return;

    const initial = { ...EMPTY_OPTIONS };
    cascadeOptions.forEach((option) => {
      if (!option.disabled) {
        initial[option.id] = true;
      }
    });
    setOptions(initial);
  }, [open, cascadeOptions]);

  if (!open) return null;

  const hasCascadeOptions = cascadeOptions.length > 0;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      await onConfirm(options);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  function toggleOption(id: keyof PlanningDuplicateCascadeOptions): void {
    setOptions((current) => {
      const next = { ...current, [id]: !current[id] };

      if (id === 'includeMetas' && !next.includeMetas) {
        next.includeMarcos = false;
      }

      if (id === 'includeMarcos' && next.includeMarcos) {
        next.includeMetas = true;
      }

      if (id === 'includeObjectives' && !next.includeObjectives) {
        next.includeMetas = false;
        next.includeMarcos = false;
        next.includeTasks = false;
      }

      return next;
    });
  }

  return (
    <ModalPortalShell open={open}>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => void handleSubmit(event)}>
        <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
          <h2 className="erp-card-title">Duplicar registro</h2>
          <p className="erp-card-subtitle">
            Confirma a duplicação de &quot;{itemLabel}&quot;? Escolha o que será copiado em cascata.
          </p>
        </div>

        <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          {hasCascadeOptions ? (
            <div className="space-y-2 rounded-lg border border-secondary-200 p-3">
              <p className="text-sm font-medium text-secondary-900">Conteúdo em cascata</p>
              {cascadeOptions.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-start gap-2 rounded-md p-2 ${
                    option.disabled ? 'opacity-50' : 'hover:bg-secondary-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={options[option.id]}
                    disabled={option.disabled}
                    onChange={() => toggleOption(option.id)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-secondary-900">
                      {option.label}
                      {option.count != null ? ` (${option.count})` : ''}
                    </span>
                    <span className="text-xs text-secondary-500">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-secondary-600">
              Será criada uma cópia deste registro. Ajuste o conteúdo depois, se necessário.
            </p>
          )}
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="erp-btn-primary" disabled={saving}>
            {saving ? 'Duplicando…' : 'Duplicar'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
